import { Button } from '@progress/kendo-react-buttons';
import { SearchInput } from 'app/components/SearchInput';
import {
  ToggleButton,
  ToggleButtonGroup,
} from 'app/components/ToggleButtonGroup';
import { EquipmentInfo } from 'app/components/access-sharing';
import {
  BackActionContentLayout,
  CardContentLayout,
} from 'app/components/layouts';
import { BaseDataGrid, DataGridColumn } from 'app/components/tables';
import { dummyEquipmentData } from 'app/constants/access-sharing';
import { useIsMobile } from 'app/hooks/useIsMobile';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ManageSubscriptionPlansPage.less';
import { SubscriptionCard } from './components';
import {
  MANAGE_SUBSCRIPTION_PLANS_FILTERS as FILTERS,
  dummySubscriptions,
} from './constants';
import { ManageSubscriptionPlansFiltersType, Subscription } from './type';
import { BaseButton } from 'app/components/buttons/BaseButton';
import { SubscriptionPlanBadge, NoActiveSubscriptions } from './components';
import { LayoutContext } from 'app/components/ReactContexts/layoutContext';
import { useManageYourPlanDialog } from 'context';

export default function ManageSubscriptionPlansPage() {
  const { t } = useTranslation();
  const { setClassNames, removeClassNames } = React.useContext(LayoutContext);
  const isMobile = useIsMobile();

  const [searchTerm, setSearchTerm] = useState('');

  const [filter, setFilter] = useState<ManageSubscriptionPlansFiltersType>(
    FILTERS.all,
  );

  const { openDialog: openManageSubscriptionPlanDialog } =
    useManageYourPlanDialog();

  // TODO throw it away as soon as possible
  // Refactor layout components in the future to be more flexible
  // and avoid hacks like this.
  React.useEffect(() => {
    let classNameIndex = -1;

    if (!isMobile) {
      classNameIndex = setClassNames('layout-grey');
    }
    return () => {
      if (classNameIndex >= 0) {
        removeClassNames(classNameIndex);
        classNameIndex = -1;
      }
    };
  }, [isMobile]);

  const filteredSubscriptions = useMemo(() => {
    if (filter === 'basicPlan') {
      return dummySubscriptions.filter(
        subscription => subscription.plan === 'basic',
      );
    }

    if (filter === 'premiumPlan') {
      return dummySubscriptions.filter(
        subscription => subscription.plan === 'premium',
      );
    }

    if (filter === 'expiringSoon') {
      return dummySubscriptions.filter(subscription => !!subscription.due);
    }

    return dummySubscriptions;
  }, [filter]);

  const subscriptionColumns = useMemo<DataGridColumn<Subscription>[]>(
    () => [
      {
        field: 'id',
        headerName: t('subscriptions.manage_plans.table.columns.equipment'),
        flex: 2,
        renderCell: row => <EquipmentInfo equipment={dummyEquipmentData[0]} />,
      },
      {
        field: 'plan',
        headerName: t('subscriptions.manage_plans.table.columns.plan'),
        renderCell: row => <SubscriptionPlanBadge value={row.plan} />,
        flex: 1,
      },
      {
        field: 'paymentType',
        headerName: t('subscriptions.manage_plans.table.columns.payment_type'),
        renderCell: row =>
          t(
            `subscriptions.manage_plans.table.rows.payment_type.${row.paymentType}`,
          ),
        flex: 1,
      },
      {
        field: 'due',
        headerName: t(
          'subscriptions.manage_plans.table.columns.renews_ends_on',
        ),
        renderCell: row => (
          <span className="manage-subscription-plans-page__due-cell">
            {row.due}
          </span>
        ),
        flex: 1,
      },
      {
        field: 'actions',
        width: 134,
        renderCell: row =>
          row?.plan === 'premium' ? (
            <BaseButton
              className="manage-subscription-plans-page__manage-cell"
              onClick={() =>
                openManageSubscriptionPlanDialog({ equipment: {} as any })
              }
            >
              {t('subscriptions.manage_plans.table.rows.manage')}
            </BaseButton>
          ) : (
            <Button
              themeColor="primary"
              className="manage-subscription-plans-page__actions-cell"
              onClick={() =>
                openManageSubscriptionPlanDialog({ equipment: {} as any })
              }
            >
              {t('subscriptions.manage_plans.table.rows.upgrade')}
            </Button>
          ),
      },
    ],
    [t],
  );

  function renderContent() {
    if (!isMobile) {
      return (
        <BaseDataGrid
          columns={subscriptionColumns}
          rows={[]}
          noDataSlot={<NoActiveSubscriptions />}
        />
      );
    }

    if (filteredSubscriptions.length >= 1) {
      return filteredSubscriptions.map((subscription, index) => (
        <SubscriptionCard
          key={subscription.id}
          subscription={subscription}
          isLast={index + 1 === filteredSubscriptions.length}
        />
      ));
    }

    return <NoActiveSubscriptions />;
  }

  return (
    <BackActionContentLayout onBack={() => {}}>
      <CardContentLayout
        title={t('subscriptions.manage_plans.title')}
        subtitle={t('subscriptions.manage_plans.subtitle')}
      >
        <div className="manage-subscription-plans-page__filters-toolbar">
          <SearchInput
            className="manage-subscription-plans-page__search-input"
            placeholder={t(
              'subscriptions.manage_plans.table.filters.search_equipment',
            )}
            value={searchTerm}
            onChange={setSearchTerm}
          />

          <ToggleButtonGroup value={filter} onChange={setFilter}>
            <ToggleButton value={FILTERS.all}>
              {t('subscriptions.manage_plans.table.filters.all')}
            </ToggleButton>

            <ToggleButton value={FILTERS.basicPlan}>
              {t('subscriptions.manage_plans.table.filters.basic_plan')}
            </ToggleButton>

            <ToggleButton value={FILTERS.premiumPlan}>
              {t('subscriptions.manage_plans.table.filters.premium_plan')}
            </ToggleButton>

            <ToggleButton value={FILTERS.expiringSoon}>
              {t('subscriptions.manage_plans.table.filters.expiring_soon')}
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            themeColor="primary"
            className="manage-subscription-plans-page__upgrade-all-button"
            onClick={() =>
              openManageSubscriptionPlanDialog({ equipment: {} as any })
            }
          >
            {t('subscriptions.manage_plans.table.filters.upgrade_all')}
          </Button>
        </div>

        {renderContent()}
      </CardContentLayout>
    </BackActionContentLayout>
  );
}
